/**
 * 图元加载接口大小来源。
 */
export type PrimitiveLoadSizeSource =
    | 'encodedBodySize'
    | 'transferSize'
    | 'decodedBodySize'
    | 'serializedData'
    | 'unknown';

/**
 * 图元加载接口统计结果。
 */
export interface PrimitiveLoadStat {
    /**
     * 接口响应大小，单位为字节。
     */
    sizeBytes: number;

    /**
     * 大小统计来源。
     */
    sizeSource: PrimitiveLoadSizeSource;

    /**
     * 图元接口请求耗时，单位为毫秒。
     */
    requestCostMs: number;

    /**
     * 实际匹配到的接口 URL。
     */
    requestUrl: string;

    /**
     * 返回图元数量。
     */
    primitiveCount: number;
}

interface CollectPrimitiveLoadStatParams {
    projectId: string;

    /**
     * 调用 findPrimitivesInGraphic 前的 performance.now()。
     */
    loadStartTime: number;

    /**
     * findPrimitivesInGraphic 返回后的 performance.now()。
     */
    loadEndTime: number;

    /**
     * 接口响应数据。
     *
     * 建议传递完整 res，而不是只传递 res.data，
     * 这样 JSON 大小兜底更接近真实接口响应。
     */
    responseData: unknown;

    primitiveCount: number;
}

/**
 * 将字节数转成可阅读文本。
 */
export const formatBytes = (
    bytes?: number,
): string => {
    if (!bytes || bytes <= 0) {
        return '未知';
    }

    const units = ['B', 'KB', 'MB', 'GB'];

    let value = bytes;
    let unitIndex = 0;

    while (
        value >= 1024 &&
        unitIndex < units.length - 1
    ) {
        value /= 1024;
        unitIndex++;
    }

    return `${value.toFixed(
        unitIndex === 0 ? 0 : 2,
    )}${units[unitIndex]}`;
};

/**
 * 将毫秒耗时转成可阅读文本。
 */
export const formatCost = (
    costMs?: number,
): string => {
    if (!costMs || costMs <= 0) {
        return '未知';
    }

    if (costMs < 1000) {
        return `${Math.round(costMs)}ms`;
    }

    return `${(costMs / 1000).toFixed(2)}s`;
};

/**
 * 计算对象重新序列化后的字节数。
 *
 * 当 PerformanceResourceTiming 无法得到接口大小时，
 * 使用它作为兜底。
 */
const getSerializedDataSize = (
    data: unknown,
): number => {
    if (data === undefined || data === null) {
        return 0;
    }

    try {
        const json = JSON.stringify(data);

        if (!json) {
            return 0;
        }

        return new TextEncoder()
            .encode(json)
            .byteLength;
    } catch (error) {
        console.warn(
            '[PRIMITIVE_LOAD_STAT] 计算响应数据大小失败:',
            error,
        );

        return 0;
    }
};

/**
 * 查找本次图元接口对应的 PerformanceResourceTiming。
 */
const findPrimitiveResourceEntry = (
    projectId: string,
    loadStartTime: number,
    loadEndTime: number,
): PerformanceResourceTiming | null => {
    if (
        typeof performance === 'undefined' ||
        !projectId
    ) {
        return null;
    }

    const expectedPathSuffix =
        `/projects/${projectId}/primitives`;

    const entries =
        performance.getEntriesByType(
            'resource',
        ) as PerformanceResourceTiming[];

    const matchedEntries = entries.filter(
        entry => {
            if (
                entry.startTime <
                loadStartTime - 5
            ) {
                return false;
            }

            if (
                entry.startTime >
                loadEndTime + 5
            ) {
                return false;
            }

            try {
                const resourceUrl =
                    new URL(
                        entry.name,
                        window.location.origin,
                    );

                return resourceUrl.pathname.endsWith(
                    expectedPathSuffix,
                );
            } catch {
                return entry.name.includes(
                    expectedPathSuffix,
                );
            }
        },
    );

    if (matchedEntries.length === 0) {
        return null;
    }

    /**
     * 若同一时间段出现多条相同接口记录，
     * 使用最后发起的一条。
     */
    return matchedEntries.sort(
        (a, b) =>
            b.startTime - a.startTime,
    )[0];
};

/**
 * 从 PerformanceResourceTiming 中读取接口大小。
 */
const getPerformanceResourceSize = (
    entry: PerformanceResourceTiming | null,
): {
    sizeBytes: number;
    sizeSource: PrimitiveLoadSizeSource;
} => {
    if (!entry) {
        return {
            sizeBytes: 0,
            sizeSource: 'unknown',
        };
    }

    /**
     * 压缩后的响应体大小，
     * 最接近实际接口传输的数据大小。
     */
    if (
        entry.encodedBodySize &&
        entry.encodedBodySize > 0
    ) {
        return {
            sizeBytes:
                entry.encodedBodySize,
            sizeSource:
                'encodedBodySize',
        };
    }

    /**
     * 包含响应头在内的传输大小。
     */
    if (
        entry.transferSize &&
        entry.transferSize > 0
    ) {
        return {
            sizeBytes:
                entry.transferSize,
            sizeSource: 'transferSize',
        };
    }

    /**
     * 浏览器解码后的响应体大小。
     */
    if (
        entry.decodedBodySize &&
        entry.decodedBodySize > 0
    ) {
        return {
            sizeBytes:
                entry.decodedBodySize,
            sizeSource:
                'decodedBodySize',
        };
    }

    return {
        sizeBytes: 0,
        sizeSource: 'unknown',
    };
};

/**
 * 统计图元加载接口大小和耗时。
 */
export const collectPrimitiveLoadStat = (
    params: CollectPrimitiveLoadStatParams,
): PrimitiveLoadStat => {
    const {
        projectId,
        loadStartTime,
        loadEndTime,
        responseData,
        primitiveCount,
    } = params;

    const resourceEntry =
        findPrimitiveResourceEntry(
            projectId,
            loadStartTime,
            loadEndTime,
        );

    let {
        sizeBytes,
        sizeSource,
    } = getPerformanceResourceSize(
        resourceEntry,
    );

    /**
     * 如果浏览器未提供网络资源大小，
     * 使用响应数据重新序列化后的大小兜底。
     */
    if (sizeBytes <= 0) {
        sizeBytes =
            getSerializedDataSize(
                responseData,
            );

        sizeSource =
            sizeBytes > 0
                ? 'serializedData'
                : 'unknown';
    }

    return {
        sizeBytes,
        sizeSource,

        requestCostMs: Math.max(
            0,
            loadEndTime - loadStartTime,
        ),

        requestUrl:
            resourceEntry?.name ||
            `${window.location.origin}/api/projects/${projectId}/primitives`,

        primitiveCount,
    };
};